import { useSessionStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, markRaw } from 'vue';
import type { WizardStep } from '../components/WizardSidebar/WizardSidebar.types';

export interface WizardStoreProp {
  activeIndex: number;
  maxReachedIndex: number;
  steps: WizardStep[];
  sharedProps?: Record<string, unknown>;
  resumable: boolean;
  completed: boolean;
}

export interface InitializeStepperOptions {
  initialStep?: number;
  resumable?: boolean;
}

interface WizardStore {
  steppers: Record<string, WizardStoreProp>;
}

export const useWizardStore = defineStore('wizard', () => {
  const steppers = useSessionStorage<WizardStore['steppers']>(
    'wizards',
    {},
    {
      serializer: {
        read: (raw) => {
          try {
            return JSON.parse(raw);
          } catch {
            return {};
          }
        },
        write: (value) =>
          JSON.stringify(value, (key, val) =>
            key === 'component' || key === 'startComponent' ? undefined : val,
          ),
      },
    },
  );

  const hasActiveWizard = computed(
    () => Object.keys(steppers.value).length > 0,
  );

  const initializeStepper = (
    namespace: string,
    steps: WizardStep[],
    options: InitializeStepperOptions = {},
  ) => {
    const { initialStep, resumable = false } = options;
    const previous = resumable ? steppers.value[namespace] : undefined;
    const activeIndex = previous?.activeIndex ?? initialStep ?? 0;
    steppers.value[namespace] = {
      activeIndex,
      maxReachedIndex: Math.max(activeIndex, previous?.maxReachedIndex ?? 0),
      steps: steps.map((step) => {
        let normalized = step;
        if (step.component) {
          normalized = { ...normalized, component: markRaw(step.component) };
        }
        if (step.footer?.startComponent) {
          normalized = {
            ...normalized,
            footer: {
              ...step.footer,
              startComponent: markRaw(step.footer.startComponent),
            },
          };
        }
        return normalized;
      }),
      sharedProps: previous?.sharedProps,
      resumable,
      completed: previous?.completed ?? false,
    };
  };

  const nextStep = (namespace: string) => {
    const stepper = steppers.value[namespace];
    if (stepper && stepper.activeIndex < stepper.steps.length - 1) {
      stepper.activeIndex++;
      stepper.maxReachedIndex = Math.max(
        stepper.maxReachedIndex,
        stepper.activeIndex,
      );
    }
  };

  const previousStep = (namespace: string) => {
    const stepper = steppers.value[namespace];
    if (stepper && stepper.activeIndex > 0) {
      stepper.activeIndex--;
    }
  };

  const goToStep = (namespace: string, stepName: string) => {
    const stepper = steppers.value[namespace];
    if (!stepper) {
      return;
    }

    const stepIndexByName = stepper.steps.findIndex(
      (step) => step.name === stepName,
    );

    if (stepIndexByName >= 0) {
      stepper.activeIndex = stepIndexByName;
      stepper.maxReachedIndex = Math.max(
        stepper.maxReachedIndex,
        stepIndexByName,
      );
    }
  };

  const setStepperProps = (
    namespace: string,
    props: Record<string, unknown>,
  ) => {
    const stepper = steppers.value[namespace];
    if (!stepper) {
      return;
    }
    stepper.sharedProps = props;
  };

  const completeStepper = (namespace: string) => {
    const stepper = steppers.value[namespace];
    if (!stepper) {
      return;
    }
    stepper.completed = true;
  };

  const destroyStepper = (namespace: string) => {
    delete steppers.value[namespace];
  };

  return {
    steppers,
    hasActiveWizard,
    initializeStepper,
    nextStep,
    previousStep,
    goToStep,
    completeStepper,
    destroyStepper,
    setStepperProps,
  };
});
